import { createSelector } from 'reselect';
import { getScopeScheduledActivityIntervals } from 'lib/selectors/getScopeScheduledActivityIntervals';
import { getHeavyUpdate } from 'lib/selectors/getUpdate';
import { getCompany } from 'lib/selectors/getCompany';
import { getAnnotations } from 'lib/selectors/getAnnotations';
import { diff, toISO } from 'lib/utils/date';
import { getIntervalSetsIntersection } from 'lib/utils/intervals';
import _get from 'lodash/get';
import _last from 'lodash/last';
import _dropRight from 'lodash/dropRight';

const getCachedActivityIntervals = createSelector(
  [
    getScopeScheduledActivityIntervals,
    getHeavyUpdate, // force recalc every fifteen seconds
  ],
  (
    activityIntervals = [],
    _heavyUpdate // eslint-disable-line no-unused-vars
  ) => {
    const lastInterval = _last(activityIntervals);
    // no need to recalc agg below unless end of last interval is open
    if (!lastInterval || _get(lastInterval, 'end')) {
      return activityIntervals;
    }
    return [
      ...(_dropRight(activityIntervals, 1) || []),
      {
        ...lastInterval,
        end: toISO(),
      },
    ];
  }
);

const getTotalScopeScheduledTimeMs = createSelector(
  // only recalc agg when necessary (last interval open)
  [getCachedActivityIntervals, getCompany, getAnnotations],
  (cachedActivityIntervals, company, annotations = []) => {
    const totalActivityTimeMs = cachedActivityIntervals.reduce(
      (aggTimeMs, interval) => {
        const intervalDuration = diff(
          interval.end,
          interval.start,
          'milliseconds'
        );
        return aggTimeMs + intervalDuration;
      },
      0
    );

    // If excludePlannedDowntime is not enabled, return the original calculation
    if (!company.excludePlannedDowntime) {
      return totalActivityTimeMs;
    }

    // Get planned downtime intervals that have both start and end times
    const plannedDowntimeIntervals = annotations
      .filter((annotation) => {
        return (
          (annotation.planned === true ||
            annotation.annotationType?.plannedBehavior ===
              'plannedWithLimit') &&
          annotation.start &&
          annotation.end
        );
      })
      .map((annotation) => {
        const { start, end, annotationType } = annotation;

        // Handle plannedWithLimit annotations
        if (annotationType?.plannedBehavior === 'plannedWithLimit') {
          const { plannedLimitMs } = annotationType;
          const totalDurationMs = diff(end, start, 'milliseconds');

          if (plannedLimitMs && totalDurationMs > plannedLimitMs) {
            // Only include the planned portion (up to the limit)
            const plannedEndTime = new Date(
              new Date(start).getTime() + plannedLimitMs
            ).toISOString();
            return {
              start,
              end: plannedEndTime,
            };
          }
        }

        return {
          start,
          end,
        };
      });

    if (!plannedDowntimeIntervals.length) {
      return totalActivityTimeMs;
    }

    // Calculate intersection between scheduled activity intervals and planned downtime
    const plannedDowntimeOverlapIntervals = getIntervalSetsIntersection(
      cachedActivityIntervals,
      plannedDowntimeIntervals
    );

    // Sum up the overlapping durations
    const plannedDowntimeOverlapMs = plannedDowntimeOverlapIntervals.reduce(
      (total, interval) => {
        const duration = diff(interval.end, interval.start, 'milliseconds');
        return total + duration;
      },
      0
    );

    // Return scheduled time minus planned downtime that actually overlapped with scheduled time
    return Math.max(0, totalActivityTimeMs - plannedDowntimeOverlapMs);
  }
);

export { getTotalScopeScheduledTimeMs };
